import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  PROJECT_KINDS, PROJECT_STATUSES, day, economics, eur, kindLabel, projectsApi, statusOf,
  type ProjectKind, type ProjectList, type ProjectStatus,
} from '../api/projects'
import {
  Button, Callout, DataTable, EmptyState, FormGrid, LoadingState, Page, PageHeader, Pagination, Select, StatCard, StatGrid, StatusPill, TextField,
} from '../components/ui'

/**
 * ADR-019 (Equal Point, card 3): elenco dei progetti del ramo Studio (siti, canoni, revenue share, commissioni,
 * lead generation). Non sono pratiche: non entrano in gare, provvigioni a pratica né crediti attesi.
 */
const PAGE_SIZE = 50

export function ProjectsPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const customerId = Number(params.get('customerId')) || undefined
  const [status, setStatus] = useState<ProjectStatus | ''>('')
  const [kind, setKind] = useState<ProjectKind | ''>('')
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [data, setData] = useState<ProjectList | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Solo l'ultima richiesta aggiorna la pagina: una risposta lenta di un filtro vecchio non sovrascrive quella nuova.
  const request = useRef(0)
  const load = useCallback(async () => {
    const mine = ++request.current
    setLoading(true)
    setError('')
    try {
      const r = await projectsApi.list({ status, kind, q: query, page, pageSize: PAGE_SIZE, ...(customerId ? { customerId } : {}) })
      if (mine === request.current) setData(r)
    } catch (e) {
      if (mine === request.current) { setData(null); setError(e instanceof Error ? e.message : 'Errore nel caricamento dei progetti.') }
    } finally {
      if (mine === request.current) setLoading(false)
    }
  }, [status, kind, query, page, customerId])

  useEffect(() => { void load() }, [load])
  useEffect(() => { setPage(1) }, [customerId])
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(1) }, 300)
    return () => clearTimeout(t)
  }, [search])

  const items = data?.items ?? []
  return (
    <Page>
      <PageHeader
        title="Progetti"
        icon="design_services"
        {...(data ? { count: data.total } : {})}
        actions={
          <Button variant="filled" icon="add" onClick={() => navigate(`/projects/new${customerId ? `?customerId=${customerId}` : ''}`)}>
            Nuovo progetto
          </Button>
        }
      />
      <div className="d-flex flex-column gap-3">
        {customerId && (
          <Callout tone="info">
            Filtrati per <Link to={`/customers/${customerId}`}>cliente n. {customerId}</Link> · <Link to="/projects">mostra tutti</Link>
          </Callout>
        )}
        {data && (
          <StatGrid>
            <StatCard label="Progetti attivi" value={data.activeCount.toLocaleString('it-IT')} />
            <StatCard label="Canoni del mese (attivi)" value={eur(data.monthlyTotal)} />
          </StatGrid>
        )}
        <FormGrid columns={3}>
          <div><TextField label="Cerca" placeholder="Titolo o cliente" value={search} onChange={e => setSearch(e.target.value)} /></div>
          <div>
            <Select label="Stato" value={status} onChange={e => { setStatus(e.target.value as ProjectStatus | ''); setPage(1) }}>
              <option value="">Tutti</option>
              {PROJECT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>
          </div>
          <div>
            <Select label="Tipo" value={kind} onChange={e => { setKind(e.target.value as ProjectKind | ''); setPage(1) }}>
              <option value="">Tutti</option>
              {PROJECT_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
            </Select>
          </div>
        </FormGrid>
        {error && <Callout tone="error">{error}</Callout>}
        {loading ? (
          <LoadingState />
        ) : error ? null : items.length === 0 ? (
          <EmptyState icon="design_services" title="Nessun progetto." />
        ) : (
          <>
            <DataTable>
              <thead>
                <tr><th>Progetto</th><th>Cliente</th><th>Tipo</th><th>Condizioni</th><th>Periodo</th><th>Stato</th></tr>
              </thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.id}>
                    <td><Link to={`/projects/${p.id}`}>{p.title}</Link></td>
                    <td><Link to={`/customers/${p.customerId}`}>{p.customerName || `Cliente n. ${p.customerId}`}</Link></td>
                    <td>{kindLabel(p.kind)}</td>
                    <td className="text-nowrap">{economics(p)}</td>
                    <td className="text-nowrap">{day(p.startDate)} – {day(p.endDate)}</td>
                    <td><StatusPill tone={statusOf(p.status).tone}>{statusOf(p.status).label}</StatusPill></td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
            <Pagination page={page} pageSize={PAGE_SIZE} total={data?.total ?? 0} onChange={setPage} />
          </>
        )}
      </div>
    </Page>
  )
}
